import { LanguageCode, SignageClockFormat, SignageUnitSystem } from '@navio/nsdk';
import type { SignageDeviceRecord } from 'noddi-async';
import { invalidateSignageQueries, noddiAsync, URLKeys } from 'noddi-async';
import { z } from 'zod';

import { NoddiSheet, useAppForm } from 'noddi-ui-web';
import { SIGNAGE_REFRESH_INTERVAL_TOOLTIP, signageRefreshIntervalSecondsSchema } from './signageContentAssign';
import { signageDeviceDisplayName } from './signageDeviceDisplay';
import { clockFormatOptions, languageOptions, unitSystemOptions } from './signageDeviceLocale';

const FORM_ID = 'signage-device-config-form';

const configSchema = z.object({
  language: z.enum(LanguageCode),
  clockFormat: z.enum(SignageClockFormat),
  unitSystem: z.enum(SignageUnitSystem),
  refreshIntervalSeconds: signageRefreshIntervalSecondsSchema
});

type ConfigFormValues = z.input<typeof configSchema>;

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  device: SignageDeviceRecord;
};

/**
 * Device preferences: language, clock, units, and config poll interval.
 * Opened from Device config so content assign stays focused on what the screen shows.
 */
export const SignageDeviceConfigSheet = ({ open, onOpenChange, device }: Props) => {
  const { mutateAsync: patchDevice } = noddiAsync.usePatch({
    type: URLKeys.patchSignageDevice,
    queryConfig: {
      onSuccess: () => {
        void invalidateSignageQueries();
        onOpenChange(false);
      }
    }
  });

  const form = useAppForm({
    defaultValues: {
      language: device.language ?? LanguageCode.NORWEGIAN_BOKMAL,
      clockFormat: device.clockFormat ?? SignageClockFormat.TWENTY_FOUR_HOUR,
      unitSystem:
        device.unitSystem === SignageUnitSystem.IMPERIAL ? SignageUnitSystem.IMPERIAL : SignageUnitSystem.METRIC,
      refreshIntervalSeconds: device.refreshIntervalSeconds ?? 60
    } satisfies ConfigFormValues,
    validators: { onSubmit: configSchema },
    onSubmit: async ({ value }) => {
      const valid = configSchema.parse(value);
      await patchDevice({
        pk: device.id,
        language: valid.language,
        clockFormat: valid.clockFormat,
        unitSystem: valid.unitSystem,
        refreshIntervalSeconds: valid.refreshIntervalSeconds
      });
    }
  });

  return (
    <form.AppForm>
      <NoddiSheet
        open={open}
        onOpenChange={onOpenChange}
        title='Device configuration'
        description={`Language, clock, units, and configuration refresh for ${signageDeviceDisplayName(device)}. Applied on the next configuration poll.`}
        footer={<form.SubscribeButton formId={FORM_ID} label='Save configuration' className='w-full' />}
      >
        <form
          id={FORM_ID}
          className='flex flex-col gap-4'
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
        >
          <form.AppField name='language'>
            {(field) => (
              <field.Select
                label='Language'
                options={languageOptions}
                tooltip='UI language for pair / empty / dashboard HTML on the TV.'
              />
            )}
          </form.AppField>
          <form.AppField name='clockFormat'>
            {(field) => (
              <field.Select
                label='Clock format'
                options={clockFormatOptions}
                tooltip='Wall-clock on the pairing and empty screens (device local timezone).'
              />
            )}
          </form.AppField>
          <form.AppField name='unitSystem'>
            {(field) => (
              <field.Select
                label='Units'
                options={unitSystemOptions}
                tooltip='Measurement system for temperature and other unit-sensitive labels on this screen.'
              />
            )}
          </form.AppField>
          <form.AppField name='refreshIntervalSeconds'>
            {(field) => (
              <field.Input
                label='Configuration refresh (seconds)'
                type='number'
                tooltip={SIGNAGE_REFRESH_INTERVAL_TOOLTIP}
              />
            )}
          </form.AppField>
        </form>
      </NoddiSheet>
    </form.AppForm>
  );
};
